import { createContext, useContext, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import i18n from './index';

type Language = 'ja' | 'en';

interface LanguageContextType {
  lang: Language;
  altLangPath: (pathname: string) => string;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

function altLangPath(pathname: string): string {
  const isEn = pathname === '/en' || pathname.startsWith('/en/');
  if (isEn) {
    // en -> ja: remove leading /en
    const base = pathname.replace(/^\/en(\/|$)/, '/');
    return base || '/';
  } else {
    // ja -> en: prepend /en
    if (pathname === '/') return '/en';
    return '/en' + pathname;
  }
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const lang: Language =
    pathname === '/en' || pathname.startsWith('/en/') ? 'en' : 'ja';

  useEffect(() => {
    i18n.changeLanguage(lang);
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, altLangPath }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguageContext(): LanguageContextType {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguageContext must be used within LanguageProvider');
  return ctx;
}
